<template>
  <div id="app" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <el-container class="full-height">
      <el-header class="header">
        <div class="left">
          <div class="logo"><img src="./assets/3P_Funding_logo.png" /></div>
          <el-button class="btn-sidebar-toggle" @click="sidebarCollapsed = !sidebarCollapsed"
            icon="fa fa-bars"></el-button>
          <el-tag type="warning" class="ml-3" v-if="notProd">ENVIRONMENT: <strong>{{ env }}</strong></el-tag>
        </div>
        <div class="right">
          <p class="text-white mt-0 mb-0 mr-3" v-if="isAuthenticated">
            {{ user ? user.fullName : '' }}
            <template v-if="allUserOffices.length">
              <el-select class="ml-3" :value="activeOffice" @input="updateActiveOffice" value-key="id" size="small"
                :disabled="allUserOffices.length === 1">
                <el-option v-for="item in formattedUserOffices" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </template>
            <el-button class="ml-3" type="text" @click="signOut" v-if="user.id !== -1">Logout</el-button>
          </p>
          <el-button type="primary" @click="userSignInDialogVisible = true" v-if="!isAuthenticated || user.id === -1">Sign
            In</el-button>
        </div>
      </el-header>
      <el-container class="menu-and-content">
        <el-aside :width="sidebarCollapsed ? '65px' : '225px'">
          <el-menu router class="menu-nav" background-color="#2D2F33" text-color="#FFF" :collapse="sidebarCollapsed"
            v-if="isAuthenticated">
            <el-menu-item index="/">
              <i class="fa fa-home" />
              <span slot="title">Home</span>
            </el-menu-item>
            <el-menu-item index="/clients">
              <i class="fa fa-users" />
              <span slot="title">Clients</span>
            </el-menu-item>
            <el-menu-item index="/offices" v-if="!isPreparer">
              <i class="fa fa-map-marker-alt" />
              <span slot="title">Offices</span>
            </el-menu-item>
            <el-submenu index="eb-program-group">
              <template slot="title">
                <i class="fa fa-money-bill" />
                <span>Early Bird Program</span>
              </template>
              <el-menu-item-group>
                <el-menu-item index="/earlyBirdApplication">
                  <i class="fa fa-plus" /> Application
                </el-menu-item>
                <el-menu-item index="/earlyBirdEnrollment">
                  <i class="fa fa-list" /> Enrollments
                </el-menu-item>
                <el-menu-item index="/w2Generator">
                  <i class="fa fa-dollar-sign" /> Paystub to W2
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <el-submenu index="raf-group">
              <template slot="title">
                <i class="fa fa-retweet" />
                <span>Refer-A-Friend</span>
              </template>
              <el-menu-item-group>
                <el-menu-item index="" @click="logReferralDialogVisible = true">
                  <i class="fa fa-plus" /> Log Referral
                </el-menu-item>
                <el-menu-item index="/raf/payout">
                  <i class="fa fa-tags" /> Payout Referrals
                </el-menu-item>
                <el-menu-item index="/raf/">
                  <i class="fa fa-list" /> Referrals
                </el-menu-item>
                <el-menu-item index="/businesses/" v-if="isAdmin && FeatureFlag.B2B_RAF">
                  <i class="fa fa-building" /> B2B Referrals
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <el-submenu index="docutax-group" v-if="isAuthenticated && docutaxIsEnabled">
              <template slot="title">
                <i class="fa fa-file-invoice" />
                <span>Docutax</span>
              </template>
              <el-menu-item-group>
                <el-menu-item index="/docutax/submission">
                  <i class="fa fa-file-invoice" /> Docutax Submissions
                </el-menu-item>
                <el-menu-item index="/docutax/request">
                  <i class="fa fa-paper-plane" /> Sent Requests
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <el-menu-item index="/transaction" v-if="!isPreparer">
              <i class="fa fa-inbox" />
              <span slot="title">Transactions</span>
            </el-menu-item>
            <el-submenu index="report-group" v-if="isManager">
              <template slot="title">
                <i class="fa fa-chart-line" />
                <span>Reports</span>
              </template>
              <el-menu-item-group>
                <el-menu-item index="/reports/dashboard">
                  <i class="fa fa-chart-line" /> Reports Dashboard
                </el-menu-item>
                <el-menu-item index="/reports/newVsReturning">
                  <i class="fa fa-chart-line" /> New vs. Returning
                </el-menu-item>
                <el-menu-item index="/reports/scheduling">
                  <i class="fa fa-clock" /> Scheduling
                </el-menu-item>
                <el-menu-item index="/reports/alerts">
                  <i class="fa fa-exclamation-triangle" /> Alerts
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <el-submenu index="settings-group" v-if="isManager">
              <template slot="title">
                <i class="fa fa-cog" />
                <span>Settings</span>
              </template>
              <el-menu-item-group>
                <el-menu-item index="/settings/user">
                  <i class="fa fa-user" /> Users
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
          </el-menu>
        </el-aside>
        <el-container>
          <el-main v-if="isAuthenticated">
            <router-view></router-view>
          </el-main>
        </el-container>
      </el-container>
    </el-container>
    <el-dialog title="Sign In" :visible.sync="userSignInDialogVisible" :show-close="false" :close-on-click-modal="false"
      :close-on-press-escape="false" width="30%">
      <user-sign-in-dialog @close="userSignInDialogVisible = false"></user-sign-in-dialog>
    </el-dialog>
    <el-dialog title="Log Referral" :visible.sync="logReferralDialogVisible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="50%">
      <log-referral-dialog @close="logReferralDialogVisible = false"></log-referral-dialog>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import map from 'lodash/map';
import UserSignInDialog from '@/components/dialog-content/UserSignIn.vue';
import LogReferralDialog from '@/components/dialog-content/LogReferral.vue';
import OfficeToRefType from '@/util/OfficeToRefType';
import FeatureFlag from './util/FeatureFlag';
import '@fortawesome/fontawesome-free/css/all.css';

const ENV = {
  PROD: 'PROD',
  TEST: 'TEST',
  LOCAL: 'LOCAL',
};

export default {
  name: 'app',
  data() {
    return {
      FeatureFlag,
      env: ENV.PROD,
      sidebarCollapsed: true,
      appReady: false,
      userSignInDialogVisible: false,
      logReferralDialogVisible: false,
      docutaxIsEnabled: true,
    };
  },
  methods: {
    ...mapActions('User', [
      'initCurrentUser',
      'updateActiveOffice',
      'signOut',
    ]),
  },
  computed: {
    ...mapGetters('User', [
      'user',
      'allUserOffices',
      'activeOffice',
      'isAuthenticated',
      'isAdmin',
      'isPreparer',
      'isManager',
    ]),
    formattedUserOffices() {
      return map(this.allUserOffices, office => OfficeToRefType(office));
    },
    notProd() {
      return this.env !== ENV.PROD;
    },
  },
  mounted() {
    if (!this.user.departmentId) {
      this.userSignInDialogVisible = true;
    }

    const hostname = window.location.hostname;
    if (hostname === 'localhost') {
      this.env = ENV.LOCAL;
    } else if (hostname.indexOf('testmmtax.3pfunding') !== -1) {
      this.env = ENV.TEST;
    }
  },
  watch: {
    isAuthenticated() {
      if (!this.isAuthenticated) {
        this.userSignInDialogVisible = true;
      }
    },
  },
  components: {
    UserSignInDialog,
    LogReferralDialog,
  },
};

</script>

<style src="./styles/ag-grid-theme.scss" lang="scss">
</style>
<style lang="scss">
body {
  margin: 0;
  font-size: 14px;
  line-height: 1.61;
  font-family: 'Open Sans', Helvetica, sans-serif;
}

[class^='el-icon-my'],
[class*=' el-icon-my'] {
  font-family: 'FontAwesome' !important;
  /* The following is based on original CSS rules of third-party library */
  font-size: inherit;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.fa {
  font-size: 1.2em;
  padding: 0 5px;
}

#app {
  height: 100vh;
  width: 100%;
  align-items: stretch;

  .el-table .cell,
  .el-dialog__body {
    word-break: break-word;
  }

  .full-height {
    height: 100vh;
  }

  .header {
    height: 60px;
    width: 100%;
    padding: 0 18px;
    background: #FFF;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 4px 8px 3px rgba(100, 100, 100, 0.1), 0 2px 8px 0px rgba(100, 100, 100, 0.1);
    z-index: 10;

    .btn-sidebar-toggle {
      padding: 5px 3px;
      margin-left: 10px;
    }

    .left {
      display: flex;
      align-items: center;
    }

    .right {
      display: flex;
      align-items: center;
    }
  }

  .menu-and-content {
    height: calc(100vh - 60px);

    .el-aside {
      overflow: visible;
      background: #2D2F33;

      .el-menu {
        border-right: 0;

        .el-menu-item-group .el-menu-item-group__title {
          padding: 0;
        }
      }
    }

    .el-main {
      .page-container {
        height: 100%;

        .grid-wrapper {
          height: 100%;
        }
      }
    }
  }

  .el-container {
    min-width: 0;
  }
}

.el-menu--vertical .el-menu--popup,
.el-menu--vertical .el-menu--popup li>div {
  padding: 0;
  margin: 0;
}

.ma-0 {
  margin: 0;
}

.mt-0 {
  margin-top: 0;
}

.mb-0 {
  margin-bottom: 0;
}

.ma-3 {
  margin: 18px;
}

.mt-3 {
  margin-top: 18px;
}

.mb-3 {
  margin-bottom: 18px;
}

.mr-3 {
  margin-right: 18px;
}

.ml-3 {
  margin-left: 18px;
}

.ml-0 {
  margin-left: 0;
}

.pa-0 {
  padding: 0;
}

.pa-3 {
  padding: 18px;
}

.pb-3 {
  padding-bottom: 18px;
}

.align-center {
  text-align: center;
}

.align-right {
  text-align: right;
}

.text-uppercased {
  text-transform: uppercase;
}

.normal-word-wrap {
  word-wrap: normal;
  word-break: normal;
}

.el-dialog {
  min-width: 360px;
}

.strike {
  text-decoration: line-through;
}

.text-italic {
  font-style: italic;
}

.router-link {
  color: #409eff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
